import React, { useEffect, useRef, useState } from "react";
import { ChatWindow } from "../types";
import PreviewDeviceFrame from "./PreviewDeviceFrame";

interface Props {
  chatWindow: ChatWindow;
  screen: string;
  onScreenChange?: (screen: string) => void;
}

function ChatIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
        fill="currentColor"
      />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor" />
    </svg>
  );
}

export default function ChatWindowSelfPreview({ chatWindow, screen, onScreenChange: _onScreenChange }: Props) {
  const sc = chatWindow.styleConfig || {};
  const lc = chatWindow.languageConfig || {};

  const launcherOnly = screen === "Launcher";
  const [isOpen, setIsOpen] = useState(!launcherOnly && screen !== "Landing");
  // Track whether the custom launcher icon has finished loading
  const [iconLoaded, setIconLoaded] = useState(false);
  const prevIconUrl = useRef<string | undefined>(undefined);

  // Reset icon loaded state when the URL changes
  useEffect(() => {
    if (sc.launcherIconUrl !== prevIconUrl.current) {
      prevIconUrl.current = sc.launcherIconUrl;
      setIconLoaded(false);
    }
  }, [sc.launcherIconUrl]);

  // Reset open state whenever the selected screen changes so the preview
  // always reflects the chosen screen. Without this, switching between
  // accordion sections (which drive screen changes) causes the chat window
  // to flash open/closed unexpectedly.
  useEffect(() => {
    setIsOpen(!launcherOnly && screen !== "Landing");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  const showOpen = !launcherOnly && isOpen;

  const primaryColor = sc.actionColor || sc.backgroundColor || "#1670E7";
  const launcherBg = sc.launcherBgColor || sc.launcherAvailableBgColor || primaryColor;
  const headerBg = sc.frameBackgroundColor || primaryColor;
  const headerText = sc.headerTextColor || sc.frameTextColor || "#ffffff";
  const chatBg = sc.backgroundColor || "#f8fafc";
  const agentBubbleBg = sc.agentBubbleBgColor || "#ffffff";
  const agentBubbleText = sc.agentBubbleTextColor || "#1e293b";
  const agentBubbleBorder = sc.agentBubbleBorderColor || "#e2e8f0";
  const visitorBubbleBg = sc.visitorBubbleBgColor || primaryColor;
  const visitorBubbleText = sc.visitorBubbleTextColor || "#ffffff";
  const btnRadius = typeof sc.buttonRadius === "number" ? sc.buttonRadius : 8;
  const windowRadius = typeof sc.windowBorderRadius === "number" ? sc.windowBorderRadius : 12;

  const launcherPosition: "Left" | "Right" = sc.launcherPosition === "Left" ? "Left" : "Right";
  const actualWidth = sc.windowWidth || 380;
  const actualHeight = sc.windowHeight && sc.windowHeight > 0 ? sc.windowHeight : 560;
  const sideSpacing = sc.launcherOffsetX ?? sc.sideSpacing ?? 20;
  const bottomSpacing = sc.launcherOffsetY ?? sc.bottomSpacing ?? 20;

  const headerTitle = lc.title || lc.welcomeTitle || "Chat with us";
  const welcomeMessage = lc.greeting || lc.welcomeSubtitle || "Hi there! How can we help you?";
  const inputPlaceholder = lc.placeholderText || "Type a message...";
  const connectingTitle = lc.connectingTitle || "Connecting you with an agent...";

  return (
    <PreviewDeviceFrame
      launcherPosition={launcherPosition}
      launcherVerticalPosition={sc.launcherVerticalPosition ?? "bottom"}
      sideSpacing={sideSpacing}
      bottomSpacing={bottomSpacing}
      actualWidth={actualWidth}
      actualHeight={actualHeight}
      windowBorderRadius={windowRadius}
      mobileFullscreen={sc.mobileFullscreen}
      mobileBreakpointPx={sc.mobileBreakpointPx}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: launcherPosition === "Left" ? "flex-start" : "flex-end", gap: 10 }}>
        {showOpen && (
          <div
            style={{
              width: actualWidth,
              height: actualHeight,
              borderRadius: windowRadius,
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              background: chatBg,
              border: sc.windowBorderWidth ? `${sc.windowBorderWidth}px solid ${sc.windowBorderColor || "#e2e8f0"}` : undefined,
            }}
          >
            {/* Header */}
            <div
              style={{
                height: 56,
                minHeight: 56,
                background: headerBg,
                display: "flex",
                alignItems: "center",
                padding: "0 12px",
                gap: 8,
                flexShrink: 0,
              }}
            >
              {sc.logoUrl ? (
                <img src={sc.logoUrl} alt="Logo" style={{ height: 24, width: "auto", objectFit: "contain", flexShrink: 0 }} />
              ) : null}
              <span style={{ color: headerText, fontWeight: 600, fontSize: 14, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {headerTitle}
              </span>
              {(screen === "Conversation" || screen === "Connecting" || screen === "ConvoList") && (
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  title="Start new chat"
                  style={{ background: "none", border: "none", cursor: "pointer", color: headerText, display: "flex", alignItems: "center", padding: 4, flexShrink: 0, opacity: 0.8 }}
                  aria-label="Start new conversation"
                >
                  {/* Home / new-chat icon */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 12l9-9 9 9M5 10v9a1 1 0 001 1h4v-5h4v5h4a1 1 0 001-1v-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                style={{ background: "none", border: "none", cursor: "pointer", color: headerText, display: "flex", alignItems: "center", padding: 4, flexShrink: 0 }}
                aria-label="Close chat"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflow: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
              {(screen === "Landing" || screen === "") && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12, padding: "24px 16px" }}>
                  <span style={{ fontSize: 32 }}>👋</span>
                  <p style={{ color: "#475569", fontSize: 14, textAlign: "center", margin: 0 }}>{welcomeMessage}</p>
                  <button
                    type="button"
                    style={{
                      background: primaryColor,
                      color: "#ffffff",
                      border: "none",
                      borderRadius: btnRadius,
                      padding: "10px 20px",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      width: "100%",
                      marginTop: 4,
                    }}
                  >
                    Start a conversation
                  </button>
                </div>
              )}

              {screen === "Conversation" && (
                <>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: primaryColor, flexShrink: 0 }} />
                    <div style={{ background: agentBubbleBg, border: `1px solid ${agentBubbleBorder}`, borderRadius: 12, padding: "8px 12px", maxWidth: "75%", fontSize: 13, color: agentBubbleText }}>
                      Hi! How can I help you today?
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexDirection: "row-reverse" }}>
                    <div style={{ background: visitorBubbleBg, borderRadius: 12, padding: "8px 12px", maxWidth: "75%", fontSize: 13, color: visitorBubbleText }}>
                      I have a question about my order
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: primaryColor, flexShrink: 0 }} />
                    <div style={{ background: agentBubbleBg, border: `1px solid ${agentBubbleBorder}`, borderRadius: 12, padding: "8px 12px", maxWidth: "75%", fontSize: 13, color: agentBubbleText }}>
                      Of course! I'd be happy to help.
                    </div>
                  </div>
                </>
              )}

              {screen === "ConvoList" && (
                <>
                  {[
                    { name: "Support", preview: "Hi! How can I help you today?", time: "2m ago" },
                    { name: "Billing", preview: "Your invoice has been sent.", time: "1h ago" },
                  ].map((row) => (
                    <div
                      key={row.name}
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        padding: "10px 8px",
                        borderBottom: "1px solid #e2e8f0",
                        cursor: "pointer",
                        borderRadius: 8,
                      }}
                    >
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: primaryColor, flexShrink: 0 }} />
                      <div style={{ flex: 1, overflow: "hidden" }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>{row.name}</div>
                        <div style={{ fontSize: 11, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.preview}</div>
                      </div>
                      <div style={{ fontSize: 10, color: "#94a3b8", flexShrink: 0 }}>{row.time}</div>
                    </div>
                  ))}
                </>
              )}

              {screen === "Connecting" && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 16, padding: 24 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      border: `3px solid #e2e8f0`,
                      borderTopColor: primaryColor,
                      animation: "vel-self-spin 0.8s linear infinite",
                    }}
                  />
                  <style>{`@keyframes vel-self-spin { to { transform: rotate(360deg); } }`}</style>
                  <p style={{ color: "#475569", fontSize: 13, textAlign: "center", margin: 0 }}>{connectingTitle}</p>
                  <p style={{ color: "#94a3b8", fontSize: 11, textAlign: "center", margin: 0 }}>Position: 1</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ borderTop: "1px solid #e2e8f0", padding: "8px 12px", background: "#ffffff", flexShrink: 0 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="text"
                  placeholder={inputPlaceholder}
                  readOnly
                  style={{
                    flex: 1,
                    border: "1px solid #e2e8f0",
                    borderRadius: btnRadius,
                    padding: "7px 10px",
                    fontSize: 12,
                    color: "#1e293b",
                    background: "#ffffff",
                    outline: "none",
                  }}
                />
                <button
                  type="button"
                  style={{
                    background: primaryColor,
                    border: "none",
                    borderRadius: btnRadius,
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#ffffff",
                    flexShrink: 0,
                  }}
                  aria-label="Send message"
                >
                  <SendIcon />
                </button>
              </div>
              {sc.showBranding !== false && (
                <div style={{ textAlign: "center", fontSize: 11, color: "#94a3b8", marginTop: 6, lineHeight: 1.5 }}>
                  Powered by<br />
                  Velaro Incorporated<br />
                  <span style={{ fontSize: 10 }}>v2.0</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Launcher bubble */}
        <button
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: launcherBg,
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: sc.launcherTextColor || sc.launcherAvailableTextColor || "#ffffff",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            flexShrink: 0,
          }}
          aria-label={showOpen ? "Close chat" : "Open chat"}
        >
          {sc.launcherIconUrl ? (
            <>
              {/* Show ChatIcon until the custom image finishes loading to avoid blank/flat bubble */}
              {!iconLoaded && <ChatIcon />}
              <img
                src={sc.launcherIconUrl}
                alt=""
                onLoad={() => setIconLoaded(true)}
                onError={() => setIconLoaded(false)}
                style={{ width: 28, height: 28, objectFit: "contain", display: iconLoaded ? "block" : "none" }}
              />
            </>
          ) : (
            <ChatIcon />
          )}
        </button>
      </div>
    </PreviewDeviceFrame>
  );
}
